/**
 * MISEルテーマ - 2048バリアント対応ハンドラー
 *
 * 高バリアント商品（251-2048件）に対してSection Rendering APIを使用し、
 * オプション選択時に動的にバリアント情報を取得・UI更新します。
 *
 * 依存: jQuery, theme.js (theme.ProductOptions)
 *
 * @version 1.0.5-selected-variant-section
 * @date 2025-12-07
 * @commit selected-variant-section
 */

(function() {
  'use strict';

  // jQueryが読み込まれるまで待機
  function initHighVariant() {
    if (typeof jQuery === 'undefined') {
      setTimeout(initHighVariant, 50);
      return;
    }

    // jQuery読み込み完了後に実行
    (function($) {
      'use strict';

  /**
   * 高バリアントハンドラークラス
   *
   * @class HighVariantHandler
   * @param {jQuery} $productElement - data-js-product 要素
   */
  class HighVariantHandler {
    constructor($productElement) {
      this.$product = $productElement;

      // data-high-variant-mode は product-options 要素にあるため、子要素から取得
      const $options = $productElement.find('[data-high-variant-mode]');
      this.isHighVariantMode = $options.attr('data-high-variant-mode') === 'true';
      this.productHandle = $options.attr('data-product-handle') || $productElement.attr('data-product-handle');
      this.sectionId = $options.attr('data-section-id');

      // デバッグモード（URLパラメータまたはtheme.debugで有効化）
      this.debug = (window.theme && window.theme.debug) ||
                   (new URLSearchParams(window.location.search).get('debug') === 'true');

      // キャッシュ: 選択履歴をメモリに保存（Map: "option1|option2|option3" => HTML）
      this.cache = new Map();

      // バリアント一覧（Product JSON APIから取得）
      this.variants = null;
      this.variantsLoading = false;
      this.variantsPromise = null;

      // デバウンスタイマー
      this.debounceTimer = null;
      this.debounceDelay = 300; // 300ms

      // ロード状態管理
      this.isLoading = false;
      this.pendingRequest = null;

      if (this.isHighVariantMode) {
        this.init();
      }
    }

    /**
     * 初期化
     */
    init() {
      if (this.debug) {
        console.log('[HighVariant] Initialized for:', this.productHandle);
      }

      // 既存のProductOptionsを無効化（高バリアントモード時は競合するため）
      this.disableLegacyProductOptions();

      this.attachEventHandlers();
      this.logAvailableOptions();

      // バリアント一覧を事前に読み込み
      this.loadVariants();
    }

    /**
     * 既存のProductOptionsを無効化
     * 高バリアントモードでは新しいハンドラーのみを使用
     */
    disableLegacyProductOptions() {
      const $productOptions = this.$product.find('[data-js-product-options]');

      if ($productOptions.length) {
        // data-js-product-options 属性を削除して既存のイベントハンドラーを無効化
        $productOptions.removeAttr('data-js-product-options');

        // イベントハンドラーを解除
        $productOptions.off('click', '[data-js-option-value]');

        if (this.debug) {
          console.log('[HighVariant] Disabled legacy ProductOptions');
        }
      }
    }

    /**
     * バリアント一覧を取得
     *
     * 取得方法:
     * 1. まずProduct JSON API (/products/{handle}.js) を試す（高速、最大250件）
     * 2. 250件の場合は、Section Rendering APIで全バリアントを取得（最大2048件）
     *
     * @returns {Promise<Array>} バリアント配列
     */
    loadVariants() {
      const self = this;

      // 既に読み込み中または読み込み済みの場合
      if (this.variantsPromise) {
        return this.variantsPromise;
      }

      if (this.debug) {
        console.log('[HighVariant] Loading variants...');
      }

      this.variantsLoading = true;

      // まずProduct JSON APIで試す
      this.variantsPromise = fetch('/products/' + this.productHandle + '.js')
        .then(function(response) {
          if (!response.ok) {
            throw new Error('Failed to load product data: ' + response.status);
          }
          return response.json();
        })
        .then(function(product) {
          self.variants = product.variants;

          if (self.debug) {
            console.log('[HighVariant] Loaded', self.variants.length, 'variants from Product JSON API');
          }

          // 250バリアント以上ある場合は、Section Rendering APIで全バリアントを取得
          if (self.variants.length >= 250) {
            if (self.debug) {
              console.log('[HighVariant] 250+ variants detected, fetching all via Section Rendering API...');
            }
            return self.loadAllVariantsViaSectionAPI();
          }

          self.variantsLoading = false;
          return self.variants;
        })
        .catch(function(error) {
          console.error('[HighVariant] Error loading variants:', error);
          self.variantsLoading = false;
          return null;
        });

      return this.variantsPromise;
    }

    /**
     * Section Rendering APIで全バリアント（2048件まで）を取得
     * Liquidで生成されたJSONを取得するため、250件の制限を回避できる
     *
     * @returns {Promise<Array>} バリアント配列
     */
    loadAllVariantsViaSectionAPI() {
      const self = this;
      const url = '/products/' + this.productHandle + '?section_id=misell-variant-json';

      if (this.debug) {
        console.log('[HighVariant] Fetching all variants from:', url);
      }

      return fetch(url)
        .then(function(response) {
          if (!response.ok) {
            throw new Error('Section API request failed: ' + response.status);
          }
          return response.text();
        })
        .then(function(text) {
          // Section Rendering APIはHTMLラッパー付きで返すため、JSONを抽出
          // レスポンス形式: <div id="shopify-section-xxx" class="shopify-section">{ JSON }</div>
          let jsonText = text.trim();

          // HTMLラッパーを除去してJSONを抽出
          const jsonMatch = jsonText.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            jsonText = jsonMatch[0];
          }

          try {
            const data = JSON.parse(jsonText);

            // データ構造を検証
            if (!data || !Array.isArray(data.variants)) {
              throw new Error('Invalid variant data structure');
            }

            self.variants = data.variants;
            self.variantsLoading = false;

            if (self.debug) {
              console.log('[HighVariant] Loaded', self.variants.length, 'variants from Section Rendering API');
            }

            return self.variants;
          } catch (parseError) {
            console.error('[HighVariant] JSON parse error:', parseError);
            console.error('[HighVariant] Response was:', jsonText.substring(0, 200));
            throw new Error('Failed to parse variant data: ' + parseError.message);
          }
        })
        .catch(function(error) {
          console.error('[HighVariant] Error loading variants via Section API:', error);
          self.variantsLoading = false;
          // Product JSON APIの結果をフォールバックとして使用
          return self.variants;
        });
    }

    /**
     * 選択されたオプション値からバリアントIDを検索
     * @param {Array<string>} selectedValues - 選択されたオプション値
     * @returns {Object|null} 見つかったバリアント、または null
     */
    findVariantByOptions(selectedValues) {
      if (!this.variants || this.variants.length === 0) {
        if (this.debug) {
          console.log('[HighVariant] No variants loaded yet');
        }
        return null;
      }

      // 正規化関数: 小文字化 + ハイフン/スペース/アンダースコアを削除
      const normalize = function(str) {
        return String(str).toLowerCase().replace(/[-_\s]/g, '');
      };

      // 選択値を正規化
      const normalizedSelected = selectedValues.map(normalize);

      if (this.debug) {
        console.log('[HighVariant] Normalized selected values:', normalizedSelected);
      }

      // バリアント配列から、全てのオプションが一致するものを検索
      const matchedVariant = this.variants.find(function(variant) {
        // variant.options は ["Red", "Size-1"] のような配列
        if (!variant.options || variant.options.length !== selectedValues.length) {
          return false;
        }

        // 全てのオプションが一致するかチェック（正規化して比較）
        return variant.options.every(function(optionValue, index) {
          return normalize(optionValue) === normalizedSelected[index];
        });
      });

      if (this.debug) {
        console.log('[HighVariant] findVariantByOptions:', selectedValues, '=>', matchedVariant ? matchedVariant.id + ' ' + matchedVariant.title : 'not found');
      }

      return matchedVariant || null;
    }

    /**
     * 利用可能なオプション値をログ出力（デバッグ用）
     */
    logAvailableOptions() {
      if (!this.debug) return;

      try {
        const optionsData = this.$product.attr('data-available-option-values');
        if (optionsData) {
          const options = JSON.parse(optionsData);
          console.log('[HighVariant] Available options:', options);
        }
      } catch (e) {
        console.warn('[HighVariant] Failed to parse options data:', e);
      }
    }

    /**
     * イベントハンドラーをアタッチ
     */
    attachEventHandlers() {
      const self = this;

      // オプション値クリックイベント
      this.$product.on('click', '[data-js-option-value]', function(e) {
        if (!self.isHighVariantMode) return;

        // 既存のtheme.jsイベントを停止（高バリアントモード時のみ）
        e.preventDefault();
        e.stopPropagation();

        self.handleOptionChange($(this));
      });

      // セレクトボックス変更イベント
      this.$product.on('change', '[data-js-option-select]', function(e) {
        if (!self.isHighVariantMode) return;

        e.preventDefault();
        e.stopPropagation();

        self.handleSelectChange($(this));
      });
    }

    /**
     * オプション値クリック時の処理
     *
     * @param {jQuery} $clickedOption - クリックされたオプション要素
     */
    handleOptionChange($clickedOption) {
      const propertyName = $clickedOption.closest('[data-property]').attr('data-property');
      const clickedValue = $clickedOption.attr('data-value');

      if (this.debug) {
        console.log('[HighVariant] handleOptionChange - property:', propertyName, 'value:', clickedValue);
      }

      // 同じプロパティの全セクションでアクティブ状態を更新（重複セクション対応）
      // グローバル検索: 一部のセクションがdata-js-product外にあるため
      $('[data-property="' + propertyName + '"]').each(function() {
        const $section = $(this);
        $section.find('[data-js-option-value]').removeClass('active');
        $section.find('[data-js-option-value][data-value="' + clickedValue + '"]').addClass('active');
      });

      if (this.debug) {
        console.log('[HighVariant] After setting active - clicked option active:', $clickedOption.hasClass('active'));
      }

      // デバウンス付きでバリアント取得
      this.debouncedFetchVariant();
    }

    /**
     * セレクトボックス変更時の処理
     *
     * @param {jQuery} $select - 変更されたセレクト要素
     */
    handleSelectChange($select) {
      const selectedValue = $select.val();

      // data-value属性でアクティブ状態を管理
      $select.find('option').removeAttr('selected');
      $select.find(`option[data-value="${selectedValue}"]`).attr('selected', 'selected');

      // デバウンス付きでバリアント取得
      this.debouncedFetchVariant();
    }

    /**
     * デバウンス付きバリアント取得
     */
    debouncedFetchVariant() {
      const self = this;

      // 既存のタイマーをクリア
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // 新しいタイマーを設定
      this.debounceTimer = setTimeout(function() {
        const selectedValues = self.getSelectedOptionValues();
        self.fetchAndUpdateVariant(selectedValues);
      }, this.debounceDelay);
    }

    /**
     * 現在選択されているオプション値を取得
     *
     * @param {boolean} useOriginal - trueの場合、data-title（元のラベル）を優先して取得
     * @returns {Array<string>} 選択されたオプション値の配列
     */
    getSelectedOptionValues(useOriginal) {
      const self = this;
      const selectedValues = [];

      // オプションセクションごとに選択値を取得
      // data-js-product-options は既に削除されているため、.product-options クラスで検索
      // 重複するセクションがある場合は最初のものだけを使用
      const processedProperties = {};

      this.$product.find('.product-options [data-property]').each(function() {
        const $section = $(this);
        const propertyName = $section.attr('data-property');
        const style = $section.attr('data-style');

        // 既に処理済みのプロパティはスキップ
        if (processedProperties[propertyName]) {
          return;
        }
        processedProperties[propertyName] = true;

        if (style === 'select') {
          // セレクトボックス
          const $selectedOption = $section.find('option:selected');
          if ($selectedOption.length && $selectedOption.is(':disabled')) {
            return; // disabled プレースホルダはスキップ
          }
          const value = useOriginal
            ? ($selectedOption.attr('data-title') || ($selectedOption.text() || '').trim() || $section.val())
            : $section.val();
          if (value) {
            selectedValues.push(value);
            if (self.debug) {
              console.log('[HighVariant] getSelectedOptionValues - select:', propertyName, '=', value, useOriginal ? '(original)' : '(sanitized)');
            }
          }
        } else {
          // ボタン形式（text, color, image, etc）
          const $active = $section.find('[data-js-option-value].active');
          if ($active.length) {
            // useOriginal=true: data-title（元のラベル）を優先、なければdata-value
            // useOriginal=false: data-value（サニタイズ済み）を使用
            const value = useOriginal
              ? ($active.attr('data-title') || $active.attr('data-value'))
              : $active.attr('data-value');
            if (value) {
              selectedValues.push(value);
              if (self.debug) {
                console.log('[HighVariant] getSelectedOptionValues - button:', propertyName, '=', value, useOriginal ? '(original)' : '(sanitized)');
              }
            }
          } else {
            if (self.debug) {
              console.log('[HighVariant] getSelectedOptionValues - no active button for:', propertyName);
            }
          }
        }
      });

      if (this.debug) {
        console.log('[HighVariant] getSelectedOptionValues result:', selectedValues, useOriginal ? '(original)' : '(sanitized)');
      }

      return selectedValues;
    }

    /**
     * 現在選択されているオプションのoption_value_idを取得（Dawn方式）
     * Shopify Section Rendering APIの option_values パラメータ用
     *
     * @returns {Array<string>} 選択されたoption_value_idの配列
     */
    getSelectedOptionValueIds() {
      const self = this;
      const optionValueIds = [];

      // Dawn方式: variant-selectsコンポーネントから取得
      const $variantSelects = this.$product.find('variant-selects');
      if ($variantSelects.length) {
        // select option[selected] または fieldset input:checked から取得
        $variantSelects.find('select option:selected, fieldset input:checked').each(function() {
          const valueId = $(this).attr('data-option-value-id');
          if (valueId) {
            optionValueIds.push(valueId);
          }
        });

        if (self.debug) {
          console.log('[HighVariant] getSelectedOptionValueIds (Dawn):', optionValueIds);
        }
        return optionValueIds;
      }

      // レガシー方式: .product-options から取得
      const processedProperties = {};

      this.$product.find('.product-options [data-property]').each(function() {
        const $section = $(this);
        const propertyName = $section.attr('data-property');
        const style = $section.attr('data-style');

        if (processedProperties[propertyName]) {
          return;
        }
        processedProperties[propertyName] = true;

        if (style === 'select') {
          // セレクトボックス
          const $selectedOption = $section.find('option:selected');
          const valueId = $selectedOption.attr('data-option-value-id');
          if (valueId) {
            optionValueIds.push(valueId);
          }
        } else {
          // ボタン形式
          const $active = $section.find('[data-js-option-value].active');
          const valueId = $active.attr('data-option-value-id');
          if (valueId) {
            optionValueIds.push(valueId);
          }
        }
      });

      if (this.debug) {
        console.log('[HighVariant] getSelectedOptionValueIds (legacy):', optionValueIds);
      }

      return optionValueIds;
    }

    /**
     * バリアント情報を取得してUI更新
     *
     * @param {Array<string>} selectedValues - 選択されたオプション値
     */
    fetchAndUpdateVariant(selectedValues) {
      const self = this;
      const cacheKey = selectedValues.join('|');

      if (this.debug) {
        console.log('[HighVariant] Fetching variant for:', selectedValues);
      }

      // 既存のリクエストをキャンセル
      if (this.pendingRequest) {
        this.pendingRequest.abort();
      }

      // ローディング表示
      this.showLoading();

      // まずバリアント一覧が読み込まれていることを確認
      this.loadVariants()
        .then(function(variants) {
          if (!variants) {
            throw new Error('Failed to load variants');
          }

          // クライアント側でバリアントIDを検索
          const matchedVariant = self.findVariantByOptions(selectedValues);

          if (!matchedVariant) {
            // Product JSON APIで見つからない場合（250バリアント以上の場合など）
            // Section Rendering APIを使用してバリアント情報を取得
            if (self.debug) {
              console.log('[HighVariant] Variant not found in cache, using Section Rendering API fallback');
            }
            return self.fetchVariantBySectionAPI(selectedValues);
          }

          if (self.debug) {
            console.log('[HighVariant] Matched variant:', matchedVariant.id, matchedVariant.title);
          }

          // キャッシュチェック
          if (self.cache.has(cacheKey)) {
            if (self.debug) {
              console.log('[HighVariant] Using cached data');
            }
            self.updateProductUIWithVariant(self.cache.get(cacheKey), matchedVariant);
            self.hideLoading();
            return;
          }

          // Section Rendering API呼び出し（UI更新用）
          // variant パラメータを使用して正しいバリアントを指定
          return self.fetchVariantDataById(matchedVariant.id)
            .then(function(html) {
              // キャッシュに保存
              self.cache.set(cacheKey, html);

              // UI更新（クライアント側で計算したバリアントIDを使用）
              self.updateProductUIWithVariant(html, matchedVariant);

              // ローディング非表示
              self.hideLoading();

              if (self.debug) {
                console.log('[HighVariant] Variant updated successfully');
              }
            });
        })
        .catch(function(error) {
          console.error('[HighVariant] Error fetching variant:', error);
          self.showError();
          self.hideLoading();
        });
    }

    /**
     * Section Rendering APIを使用してバリアント情報を取得（フォールバック）
     * Product JSON APIで見つからない場合（250+バリアント）に使用
     *
     * Dawn方式: option_valuesパラメータを使用してShopify側でバリアントを解決
     * これにより250件制限を回避できる
     *
     * @param {Array<string>} selectedValues - 選択されたオプション値
     * @returns {Promise<void>}
     */
    fetchVariantBySectionAPI(selectedValues) {
      const self = this;

      // option_value_idを取得（Dawn方式）
      const optionValueIds = this.getSelectedOptionValueIds();

      if (this.debug) {
        console.log('[HighVariant] Section API with option_values:', optionValueIds);
        console.log('[HighVariant] Selected values:', selectedValues);
      }

      // option_value_idが取得できた場合はoption_valuesパラメータを使用
      // 注意: 実際のShopify IDは数字のみ（例: 40889086812338）
      // フォールバック形式（例: "1-0"）はハイフンを含むため無効
      const hasAllOptionsSelected = selectedValues.length > 0 &&
        optionValueIds.length === selectedValues.length;
      const hasValidOptionValueIds = optionValueIds.length > 0 &&
        optionValueIds.every(function(id) {
          // 数字のみで構成されているかチェック（フォールバック形式を除外）
          return id && /^\d+$/.test(id);
        });

      if (hasValidOptionValueIds && hasAllOptionsSelected) {
        const effectiveSectionId = this.sectionId || 'main-product';
        const params = new URLSearchParams({
          section_id: effectiveSectionId,
          option_values: optionValueIds.join(',')
        });

        const url = '/products/' + this.productHandle + '?' + params.toString();

        if (this.debug) {
          console.log('[HighVariant] Fetching with option_values:', url);
        }

        return fetch(url)
          .then(function(response) {
            if (!response.ok) {
              throw new Error('Network response was not ok: ' + response.status);
            }
            return response.text();
          })
          .then(function(html) {
            // レスポンスからバリアント情報を抽出
            const variant = self.extractVariantFromHtml(html);

            if (variant) {
              if (self.debug) {
                console.log('[HighVariant] Variant found via option_values:', variant.id, variant.title);
              }

              // UI更新
              self.updateProductUIWithVariant(html, variant);
              self.hideLoading();
            } else {
              // バリアントが見つからない場合（組み合わせが存在しない）
              if (self.debug) {
                console.log('[HighVariant] Variant not found in response');
              }
              self.handleVariantNotFound();
              self.hideLoading();
            }
          })
          .catch(function(error) {
            console.error('[HighVariant] Error fetching with option_values:', error);
            self.handleVariantNotFound();
            self.hideLoading();
          });
      }

      // option_value_idがない場合はoption1/2/3パラメータを使用（Dawn方式のフォールバック）
      // 専用セクション (misell-selected-variant) でShopifyサーバー側でバリアントを解決
      if (this.debug) {
        console.log('[HighVariant] No valid option_value_ids, using option1/2/3 fallback');
      }

      // misell-selected-variant セクションを使用（selected_or_first_available_variantを返す）
      const params = new URLSearchParams({
        section_id: 'misell-selected-variant'
      });

      // option1, option2, option3 パラメータを追加
      // 重要: フォールバックではオリジナルのラベル（data-title）を使用する
      // Shopifyのoption1/2/3はサニタイズされていない元のラベルを期待するため
      const originalValues = this.getSelectedOptionValues(true);
      originalValues.forEach(function(value, index) {
        const optionNum = index + 1; // option1, option2, option3
        params.append('option' + optionNum, value);
      });

      const url = '/products/' + this.productHandle + '?' + params.toString();

      if (this.debug) {
        console.log('[HighVariant] Fetching with option1/2/3 fallback:', url);
      }

      return fetch(url)
        .then(function(response) {
          if (!response.ok) {
            throw new Error('Network response was not ok: ' + response.status);
          }
          return response.text();
        })
        .then(function(text) {
          if (self.debug) {
            console.log('[HighVariant] option1/2/3 response:', text.substring(0, 500));
          }

          // HTMLラッパーを除去してJSONを抽出
          let jsonText = text.trim();
          const jsonMatch = jsonText.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            jsonText = jsonMatch[0];
          }

          try {
            const data = JSON.parse(jsonText);

            if (data && data.variant && data.variant.id) {
              const variant = data.variant;

              if (self.debug) {
                console.log('[HighVariant] Variant found via option1/2/3:', variant.id, variant.title);
              }

              // UI更新（バリアント情報のみで更新、HTMLは使わない）
              self.updateProductUIWithVariantData(variant);
              self.hideLoading();
            } else {
              if (self.debug) {
                console.log('[HighVariant] No variant in response:', data);
              }
              self.handleVariantNotFound();
              self.hideLoading();
            }
          } catch (e) {
            console.error('[HighVariant] Error parsing JSON:', e);
            self.handleVariantNotFound();
            self.hideLoading();
          }
        })
        .catch(function(error) {
          console.error('[HighVariant] Error fetching with option1/2/3:', error);
          self.handleVariantNotFound();
          self.hideLoading();
        });
    }

    /**
     * バリアントデータを使用してUI更新（HTMLなし）
     *
     * @param {Object} variant - バリアントオブジェクト
     */
    updateProductUIWithVariantData(variant) {
      if (!variant || !variant.id) {
        this.handleVariantNotFound();
        return;
      }

      if (this.debug) {
        console.log('[HighVariant] Updating UI with variant data:', variant.id);
      }

      // 価格更新（variantオブジェクトから直接更新）
      this.updatePriceFromVariant(variant);

      // バリアントID更新（clone先も同期）
      this.updateVariantIdDirectly(variant);

      // ボタン状態更新
      this.updateAddToCartButtonFromVariant(variant);

      // SKU更新（表示されている場合）
      this.updateSKUFromVariant(variant);

      // 画像更新（featured_imageがある場合）
      this.updateProductImageFromVariant(variant);
    }

    /**
     * HTMLレスポンスからバリアント情報を抽出
     *
     * @param {string} html - Section Rendering APIのレスポンスHTML
     * @returns {Object|null} バリアントオブジェクト
     */
    extractVariantFromHtml(html) {
      try {
        // DOMParserでHTMLをパース
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Dawn方式: [data-selected-variant]からバリアントJSONを取得
        const selectedVariantEl = doc.querySelector('[data-selected-variant]');
        if (selectedVariantEl && selectedVariantEl.innerHTML) {
          const variant = JSON.parse(selectedVariantEl.innerHTML);
          if (this.debug) {
            console.log('[HighVariant] Extracted variant from data-selected-variant:', variant);
          }
          return variant;
        }

        // レガシー方式: select[name="id"]の値を取得
        const variantSelect = doc.querySelector('select[name="id"]');
        if (variantSelect && variantSelect.value) {
          const variantId = parseInt(variantSelect.value, 10);
          // 最低限の情報を持つバリアントオブジェクトを返す
          return {
            id: variantId,
            available: true // セクションが返ってきた時点で利用可能とみなす
          };
        }

        // input[name="id"]も確認
        const variantInput = doc.querySelector('input[name="id"]');
        if (variantInput && variantInput.value) {
          const variantId = parseInt(variantInput.value, 10);
          return {
            id: variantId,
            available: true
          };
        }
      } catch (e) {
        console.error('[HighVariant] Error extracting variant from HTML:', e);
      }

      return null;
    }

    /**
     * バリアントが見つからない場合の処理
     */
    handleVariantNotFound() {
      const $addToCartButton = this.$product.find('[data-js-product-button-add-to-cart]');
      if ($addToCartButton.length) {
        $addToCartButton.prop('disabled', true);
        $addToCartButton.attr('data-button-status', 'unavailable');
      }

      if (this.debug) {
        console.log('[HighVariant] Variant not found - button disabled');
      }
    }

    /**
     * Section Rendering APIでバリアント情報を取得（バリアントIDを指定）
     *
     * @param {number} variantId - バリアントID
     * @returns {Promise<string>} レンダリングされたHTML
     */
    fetchVariantDataById(variantId) {
      const self = this;

      // URLパラメータを構築
      // コレクションページの場合は main-product セクションを使用
      const effectiveSectionId = this.sectionId || 'main-product';
      const params = new URLSearchParams({
        section_id: effectiveSectionId,
        variant: variantId
      });

      const url = '/products/' + this.productHandle + '?' + params.toString();

      if (this.debug) {
        console.log('[HighVariant] Fetching section with variant ID:', url);
      }

      // fetch APIでリクエスト（Abort対応）
      const controller = new AbortController();
      this.pendingRequest = controller;

      return fetch(url, {
        signal: controller.signal
      })
        .then(function(response) {
          if (!response.ok) {
            throw new Error('Network response was not ok: ' + response.status);
          }
          return response.text();
        })
        .finally(function() {
          self.pendingRequest = null;
        });
    }

    /**
     * Section Rendering APIでバリアント情報を取得（オプション値を指定）
     * ※現在は使用していないが互換性のために残す
     *
     * @param {Array<string>} selectedValues - 選択されたオプション値
     * @returns {Promise<string>} レンダリングされたHTML
     */
    fetchVariantData(selectedValues) {
      const self = this;

      // URLパラメータを構築
      // コレクションページの場合は main-product セクションを使用
      // （元のsectionIdはコレクションセクションのIDなので製品情報を取得できない）
      const effectiveSectionId = this.sectionId || 'main-product';
      const params = new URLSearchParams({
        section_id: effectiveSectionId
      });

      // オプション値をパラメータに追加
      selectedValues.forEach(function(value, index) {
        params.append('option' + (index + 1), value);
      });

      const url = '/products/' + this.productHandle + '?' + params.toString();

      // fetch APIでリクエスト（Abort対応）
      const controller = new AbortController();
      this.pendingRequest = controller;

      return fetch(url, {
        signal: controller.signal
      })
        .then(function(response) {
          if (!response.ok) {
            throw new Error('Network response was not ok: ' + response.status);
          }
          return response.text();
        })
        .finally(function() {
          self.pendingRequest = null;
        });
    }

    /**
     * 取得したHTMLでUI更新（バリアントオブジェクト付き）
     *
     * @param {string} html - Section Rendering APIから取得したHTML
     * @param {Object} variant - クライアント側で検索したバリアントオブジェクト
     */
    updateProductUIWithVariant(html, variant) {
      // XSS対策: DOMParserを使用してHTMLを安全にパース
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const $tempDiv = $(doc.body);
      const $newProduct = $tempDiv.find('[data-js-product]').first();

      if (!$newProduct.length) {
        // レスポンスにproduct要素がない場合でも、バリアントIDは更新
        if (this.debug) {
          console.log('[HighVariant] No product element in response, updating variant ID directly');
        }
        this.updateVariantIdDirectly(variant);
        this.$product.trigger('variant:change', [variant.id]);
        return;
      }

      // 1. 価格更新（バリアント情報から直接更新）
      this.updatePriceFromVariant(variant);

      // 2. バリアントID更新（クライアント側で計算したIDを使用）
      this.updateVariantIdDirectly(variant);

      // 3. カートボタン状態更新（バリアントの在庫状況から）
      this.updateAddToCartButtonFromVariant(variant);

      // 4. 画像更新（必要に応じて）
      this.updateProductImage($newProduct);

      // 5. SKU更新（表示されている場合）
      this.updateSKUFromVariant(variant);

      // 6. オプションボタンの状態を更新（スキップ - 既にhandleOptionChangeで設定済み）
      // this.updateOptionStates($newProduct);

      // 7. カスタムイベント発火（既存コードとの連携）
      // 注意: このイベントは既存のtheme.jsがオプション状態をリセットする可能性があるため、
      // コレクションページでは発火しない
      // this.$product.trigger('variant:change', [variant.id]);

      if (this.debug) {
        console.log('[HighVariant] UI updated with variant:', variant.id, variant.title);
      }
    }

    /**
     * 取得したHTMLでUI更新
     * ※互換性のために残すが、新しいupdateProductUIWithVariantを推奨
     *
     * @param {string} html - Section Rendering APIから取得したHTML
     */
    updateProductUI(html) {
      // XSS対策: DOMParserを使用してHTMLを安全にパース
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const $tempDiv = $(doc.body);
      const $newProduct = $tempDiv.find('[data-js-product]').first();

      if (!$newProduct.length) {
        console.error('[HighVariant] Could not find product element in response');
        return;
      }

      // 1. 価格更新
      this.updatePrice($newProduct);

      // 2. バリアントID更新
      this.updateVariantId($newProduct);

      // 3. カートボタン状態更新
      this.updateAddToCartButton($newProduct);

      // 4. 画像更新（必要に応じて）
      this.updateProductImage($newProduct);

      // 5. SKU更新（表示されている場合）
      this.updateSKU($newProduct);

      // 6. オプションボタンの状態を更新
      this.updateOptionStates($newProduct);

      // 7. カスタムイベント発火（既存コードとの連携）
      const variantId = $newProduct.find('select[name="id"]').val();
      this.$product.trigger('variant:change', [variantId]);
    }

    /**
     * 価格を更新
     *
     * @param {jQuery} $newProduct - 新しい商品要素
     */
    updatePrice($newProduct) {
      const $newPrice = $newProduct.find('[data-js-product-price]');
      if ($newPrice.length) {
        this.$product.find('[data-js-product-price]').html($newPrice.html());
      }
    }

    /**
     * バリアントオブジェクトから価格を更新
     *
     * @param {Object} variant - バリアントオブジェクト
     */
    updatePriceFromVariant(variant) {
      const $priceElement = this.$product.find('[data-js-product-price]');
      if (!$priceElement.length) return;

      // Shopifyの価格フォーマット（centで管理されているため100で割る）
      const formatMoney = function(cents) {
        // Priority 1: テーマの通貨コンバーター（マルチ通貨アプリ対応）
        if (window.theme && window.theme.Currency && window.theme.Currency.formatMoney) {
          return window.theme.Currency.formatMoney(cents);
        }

        // Priority 2: Shopify組み込みフォーマッター
        if (window.Shopify && window.Shopify.formatMoney) {
          const format = (window.theme && window.theme.moneyFormat)
            ? window.theme.moneyFormat
            : '{{amount}}';
          return window.Shopify.formatMoney(cents, format);
        }

        // Priority 3: 基本フォールバック（通貨記号なし）
        return Math.floor(cents / 100).toLocaleString();
      };

      // 比較価格がある場合（セール表示）
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        $priceElement.html(
          '<span class="price--sale">' + formatMoney(variant.price) + '</span>' +
          '<span class="price--compare"><s>' + formatMoney(variant.compare_at_price) + '</s></span>'
        );
      } else {
        $priceElement.html('<span>' + formatMoney(variant.price) + '</span>');
      }

      if (this.debug) {
        console.log('[HighVariant] Price updated:', variant.price);
      }
    }

    /**
     * バリアントIDを更新
     *
     * @param {jQuery} $newProduct - 新しい商品要素
     */
    updateVariantId($newProduct) {
      const variantId = $newProduct.find('select[name="id"]').val();
      if (variantId) {
        this.$product.find('select[name="id"]').val(variantId);
        this.$product.attr('data-product-variant-id', variantId);
      }
    }

    /**
     * バリアントオブジェクトからバリアントIDを直接更新
     *
     * @param {Object} variant - バリアントオブジェクト
     */
    updateVariantIdDirectly(variant) {
      const variantId = variant.id;

      // select[name="id"] を更新
      this.$product.find('select[name="id"]').val(variantId);

      // hidden input[name="id"] も更新（コレクションページのフォーム用）
      this.$product.find('input[name="id"]').val(variantId);

      // data属性を更新
      this.$product.attr('data-product-variant-id', variantId);

      // フッターバーなど clone の場合、メイン商品へ同期
      this.syncVariantToCloneProduct(variant);

      if (this.debug) {
        console.log('[HighVariant] Variant ID updated to:', variantId);
      }
    }

    /**
     * フッターバーなど clone の場合、メイン商品へバリアントID/状態を同期
     *
     * @param {Object} variant - バリアントオブジェクト
     */
    syncVariantToCloneProduct(variant) {
      const variantId = variant && variant.id;
      if (!variantId) return;

      const cloneId = this.$product.attr('data-js-product-clone');
      if (!cloneId) return;

      const isDebug = this.debug;

      const $cloneProduct = $('[data-js-product-clone-id="' + cloneId + '"]').first();
      if (!$cloneProduct.length) return;

      // data属性を更新
      $cloneProduct.attr('data-product-variant-id', variantId);

      // Legacy: hidden input (data-js-product-variant-input)
      const $legacyVariantInput = $cloneProduct.find('[data-js-product-variant-input]').first();
      if ($legacyVariantInput.length) {
        $legacyVariantInput.val(variantId);
        try {
          $legacyVariantInput[0].dispatchEvent(new Event('change', { bubbles: true }));
        } catch (e) {
          if (isDebug) console.warn('[HighVariant] Event dispatch failed (data-js-product-variant-input):', e);
        }
      }

      // Dawn: hidden input (data-dawn-variant-id)
      const $dawnVariantInput = $cloneProduct.find('input[data-dawn-variant-id]').first();
      if ($dawnVariantInput.length) {
        $dawnVariantInput.val(variantId);
        try {
          $dawnVariantInput[0].dispatchEvent(new Event('change', { bubbles: true }));
        } catch (e) {
          if (isDebug) console.warn('[HighVariant] Event dispatch failed (data-dawn-variant-id):', e);
        }
      }

      // 互換: input[name="id"] を全て更新（form属性のものも含む）
      $cloneProduct.find('input[name="id"]').each(function() {
        this.value = variantId;
        try {
          this.dispatchEvent(new Event('change', { bubbles: true }));
        } catch (e) {
          if (isDebug) console.warn('[HighVariant] Event dispatch failed (input[name=\"id\"]):', e);
        }
      });

      // select[name="id"] は option が存在する場合のみ更新（250件制限対策）
      $cloneProduct.find('select[name="id"]').each(function() {
        const optionExists = Array.prototype.some.call(this.options, function(opt) {
          return opt && opt.value == variantId;
        });
        if (optionExists) {
          this.value = variantId;
          try {
            this.dispatchEvent(new Event('change', { bubbles: true }));
          } catch (e) {
            if (isDebug) console.warn('[HighVariant] Event dispatch failed (select[name=\"id\"]):', e);
          }
        }
      });

      // カートボタン状態を同期（クリック先のメインボタンが無効にならないようにする）
      const $cloneAddToCartButton = $cloneProduct.find('[data-js-product-button-add-to-cart]').first();
      if ($cloneAddToCartButton.length) {
        const isAvailable = variant.available !== false;
        $cloneAddToCartButton
          .prop('disabled', !isAvailable)
          .toggleClass('disabled', !isAvailable);

        if (isAvailable) {
          $cloneAddToCartButton.removeAttr('data-button-status');
        } else {
          $cloneAddToCartButton.attr('data-button-status', 'sold-out');
        }
      }

      // 画像も同期（フッターバー操作時にメイン画像が古いままになるのを防ぐ）
      this.updateProductImageFromVariant(variant, $cloneProduct);

      if (this.debug) {
        console.log('[HighVariant] Synced variant to clone product:', cloneId, variantId);
      }
    }

    /**
     * カートボタンの状態を更新
     *
     * @param {jQuery} $newProduct - 新しい商品要素
     */
    updateAddToCartButton($newProduct) {
      const $newButton = $newProduct.find('[data-js-product-button-add-to-cart]');
      const $currentButton = this.$product.find('[data-js-product-button-add-to-cart]');

      if ($newButton.length && $currentButton.length) {
        const isAvailable = !$newButton.prop('disabled');

        $currentButton
          .prop('disabled', !isAvailable)
          .toggleClass('disabled', !isAvailable);

        // ボタンテキスト更新 - 全体のHTMLを置き換えて重複を防ぐ
        const newButtonHtml = $newButton.html();
        $currentButton.html(newButtonHtml);
      }
    }

    /**
     * バリアントオブジェクトからカートボタンの状態を更新
     *
     * @param {Object} variant - バリアントオブジェクト
     */
    updateAddToCartButtonFromVariant(variant) {
      const $currentButton = this.$product.find('[data-js-product-button-add-to-cart]');
      if (!$currentButton.length) return;

      const isAvailable = variant.available;

      $currentButton
        .prop('disabled', !isAvailable)
        .toggleClass('disabled', !isAvailable);

      // ボタンステータス属性を更新
      if (isAvailable) {
        $currentButton.removeAttr('data-button-status');
      } else {
        $currentButton.attr('data-button-status', 'sold-out');
      }

      if (this.debug) {
        console.log('[HighVariant] Button state updated, available:', isAvailable);
      }
    }

    /**
     * 商品画像を更新
     *
     * @param {jQuery} $newProduct - 新しい商品要素
     */
    updateProductImage($newProduct) {
      // メイン画像を更新
      const $newMainImage = $newProduct.find('[data-js-product-gallery-main] img').first();
      const $currentMainImage = this.$product.find('[data-js-product-gallery-main] img').first();

      if ($newMainImage.length && $currentMainImage.length) {
        const newSrc = $newMainImage.attr('src');
        const newSrcset = $newMainImage.attr('srcset');
        const newAlt = $newMainImage.attr('alt');

        if (newSrc) {
          $currentMainImage.attr('src', newSrc);
        }
        if (newSrcset) {
          $currentMainImage.attr('srcset', newSrcset);
        }
        if (newAlt) {
          $currentMainImage.attr('alt', newAlt);
        }
      }

      // featured_imageメタデータを更新（既存のtheme.jsとの互換性）
      const newFeaturedImage = $newProduct.attr('data-product-featured-image');
      if (newFeaturedImage) {
        this.$product.attr('data-product-featured-image', newFeaturedImage);
      }
    }

    /**
     * バリアントオブジェクトのfeatured_imageから商品画像を更新（HTMLなし）
     *
     * @param {Object} variant - バリアントオブジェクト
     * @param {jQuery} $targetProduct - 更新対象の商品要素（省略時はthis.$product）
     */
    updateProductImageFromVariant(variant, $targetProduct) {
      const featuredImage = variant && variant.featured_image;
      if (!featuredImage) return;

      const $product = ($targetProduct && $targetProduct.length) ? $targetProduct : this.$product;
      if (!$product || !$product.length) return;

      let src = null;
      if (typeof featuredImage === 'string') {
        src = featuredImage;
      } else if (featuredImage && typeof featuredImage === 'object') {
        src = featuredImage.src || featuredImage.url || featuredImage.original_src;
      }
      if (!src) return;

      const isDebug = this.debug;
      const alt = (featuredImage && featuredImage.alt) ? featuredImage.alt : (variant && variant.title ? variant.title : '');

      const buildMasterSrc = function(url) {
        if (!url || typeof url !== 'string') return null;

        const parts = url.split('?');
        const base = parts[0];
        const query = parts.length > 1 ? '?' + parts.slice(1).join('?') : '';
        const extMatch = base.match(/(\.[a-zA-Z0-9]+)$/);
        if (!extMatch) return url;

        const ext = extMatch[1];
        let basename = base.slice(0, -ext.length);
        basename = basename.replace(/_(\d+x\d+|\d+x|\{width\}x)$/i, '');
        return basename + '_{width}x' + ext + query;
      };

      const updateImage = function($img) {
        if (!$img || !$img.length) return;

        const element = $img[0];
        const masterSrc = buildMasterSrc(src) || src;

        const getFirstUrlFromSrcset = function(value) {
          if (!value || typeof value !== 'string') return null;

          const firstPart = value.split(',')[0].trim();
          if (!firstPart) return null;

          return firstPart.split(/\s+/)[0];
        };

        let srcsetValue = src;
        if (window.theme && theme.LazyImage && typeof theme.LazyImage.buildSrcset === 'function') {
          try {
            srcsetValue = theme.LazyImage.buildSrcset(element, masterSrc);
          } catch (e) {
            if (isDebug) console.warn('[HighVariant] buildSrcset failed:', e);
            srcsetValue = src;
          }
        }

        const srcValue = getFirstUrlFromSrcset(srcsetValue) || src;

        $img.attr('src', srcValue);
        $img.attr('srcset', srcsetValue);
        $img.attr('data-src', srcValue);
        $img.attr('data-srcset', srcsetValue);
        $img.attr('data-master', masterSrc);

        if (alt) {
          $img.attr('alt', alt);
        }
        if (featuredImage && featuredImage.id) {
          $img.attr('data-image-id', featuredImage.id);
        }
      };

      updateImage($product.find('[data-js-product-gallery-main] img').first());
      updateImage($product.find('[data-js-product-image] img').first());

      if (featuredImage && featuredImage.id) {
        $product.attr('data-product-featured-image', featuredImage.id);
      } else {
        $product.attr('data-product-featured-image', src);
      }

      if (this.debug) {
        console.log('[HighVariant] Image updated from variant data:', src);
      }
    }

    /**
     * SKUを更新
     *
     * @param {jQuery} $newProduct - 新しい商品要素
     */
    updateSKU($newProduct) {
      const $newSKU = $newProduct.find('[data-js-product-sku] span');
      const $currentSKU = this.$product.find('[data-js-product-sku] span');

      if ($newSKU.length && $currentSKU.length) {
        $currentSKU.text($newSKU.text());
      }
    }

    /**
     * バリアントオブジェクトからSKUを更新
     *
     * @param {Object} variant - バリアントオブジェクト
     */
    updateSKUFromVariant(variant) {
      const $currentSKU = this.$product.find('[data-js-product-sku] span');
      if ($currentSKU.length && variant.sku) {
        $currentSKU.text(variant.sku);
      }
    }

    /**
     * オプションボタンの状態を更新
     *
     * @param {jQuery} $newProduct - 新しい商品要素
     */
    updateOptionStates($newProduct) {
      const self = this;

      // コレクションページなど、製品ページ以外ではオプション状態の同期をスキップ
      // （レスポンスのオプション状態はリクエストしたオプション値を反映していないため）
      // 代わりに、handleOptionChangeで設定したactive状態を維持する
      if (this.debug) {
        console.log('[HighVariant] updateOptionStates - skipping to preserve current selection');
      }
      return;

      // 以下は製品ページ専用のコード（現在は無効化）
      // 新しいHTMLから全てのオプションセクションを取得
      const $newSections = $newProduct.find('.product-options [data-property]');

      if (this.debug) {
        console.log('[HighVariant] updateOptionStates - found sections in response:', $newSections.length);
      }

      // レスポンスにオプションセクションがない場合はスキップ
      if ($newSections.length === 0) {
        if (this.debug) {
          console.log('[HighVariant] updateOptionStates - no option sections in response, skipping');
        }
        return;
      }

      $newSections.each(function() {
        const $newSection = $(this);
        const propertyName = $newSection.attr('data-property');
        const style = $newSection.attr('data-style');

        if (style === 'select') {
          // セレクトボックスの場合: 全ての対応するセレクトを同期
          const selectedValue = $newSection.val();
          if (selectedValue) {
            $('[data-property="' + propertyName + '"]').each(function() {
              const $currentSection = $(this);
              if ($currentSection.is('select')) {
                $currentSection.val(selectedValue);
              }
            });
          }
        } else {
          // ボタン形式の場合: active状態とdisabled状態を同期
          $newSection.find('[data-js-option-value]').each(function() {
            const $newOption = $(this);
            const optionValue = $newOption.attr('data-value');

            // グローバル検索: 全ての対応するオプションボタンを更新（重複セクション対応）
            $('[data-property="' + propertyName + '"] [data-js-option-value][data-value="' + optionValue + '"]').each(function() {
              const $currentOption = $(this);

              // active状態を同期
              if ($newOption.hasClass('active')) {
                $currentOption.addClass('active');
              } else {
                $currentOption.removeClass('active');
              }

              // disabled状態を同期
              if ($newOption.hasClass('disabled') || $newOption.prop('disabled')) {
                $currentOption.addClass('disabled').prop('disabled', true);
              } else {
                $currentOption.removeClass('disabled').prop('disabled', false);
              }

              // hidden状態を同期
              if ($newOption.hasClass('hidden') || $newOption.css('display') === 'none') {
                $currentOption.addClass('hidden').hide();
              } else {
                $currentOption.removeClass('hidden').show();
              }
            });
          });
        }
      });

      if (this.debug) {
        console.log('[HighVariant] Option states updated (all duplicate sections)');
      }
    }

    /**
     * ローディング表示
     */
    showLoading() {
      this.isLoading = true;
      this.$product.addClass('is-loading');

      // カートボタンを一時的に無効化
      this.$product.find('[data-js-product-button-add-to-cart]')
        .prop('disabled', true)
        .addClass('loading');
    }

    /**
     * ローディング非表示
     */
    hideLoading() {
      this.isLoading = false;
      this.$product.removeClass('is-loading');

      // ローディングクラスを削除（disabled状態は在庫状況に応じて維持）
      this.$product.find('[data-js-product-button-add-to-cart]')
        .removeClass('loading');
    }

    /**
     * エラー表示
     */
    showError() {
      // data属性から多言語対応エラーメッセージを取得
      const errorMessage = this.$product.attr('data-error-message') ||
                          'Failed to load product information. Please reload the page.';

      // theme.Notificationsが存在し、showメソッドがある場合は通知表示
      if (window.theme && window.theme.Notifications && typeof window.theme.Notifications.show === 'function') {
        window.theme.Notifications.show({
          type: 'error',
          message: errorMessage
        });
      } else {
        // フォールバック: コンソールに出力（alertは煩わしいので避ける）
        console.warn('[HighVariant] Error:', errorMessage);
      }
    }
  }

  // グローバルに公開
  window.theme = window.theme || {};
  window.theme.HighVariantHandler = HighVariantHandler;

  /**
   * 高バリアント要素を初期化する関数
   */
  function initializeHighVariantElements() {
    const debug = (window.theme && window.theme.debug) ||
                  (new URLSearchParams(window.location.search).get('debug') === 'true');

    // data-high-variant-mode は product-options 要素にある
    // data-js-product は親の single-product 要素にある
    const $highVariantOptions = $('[data-high-variant-mode="true"]');

    if (debug) {
      console.log('[HighVariant] Found elements:', $highVariantOptions.length);
    }

    $highVariantOptions.each(function() {
      const $options = $(this);

      // 既に初期化済みかチェック
      if ($options.attr('data-high-variant-initialized')) {
        if (debug) {
          console.log('[HighVariant] Skipping already initialized element');
        }
        return;
      }

      // 親の [data-js-product] 要素を探す
      const $productElement = $options.closest('[data-js-product]');
      if ($productElement.length) {
        // product要素も既に初期化済みかチェック（重複防止）
        if ($productElement.attr('data-high-variant-handler-initialized')) {
          if (debug) {
            console.log('[HighVariant] Skipping already initialized product element');
          }
          $options.attr('data-high-variant-initialized', 'true');
          return;
        }

        new HighVariantHandler($productElement);
        // 初期化済みマークを付ける
        $options.attr('data-high-variant-initialized', 'true');
        $productElement.attr('data-high-variant-handler-initialized', 'true');

        if (debug) {
          console.log('[HighVariant] Initialized for:', $productElement.attr('data-product-handle'));
        }
      } else if (debug) {
        console.log('[HighVariant] No parent [data-js-product] found for:', $options);
      }
    });

    if (debug) {
      console.log('[HighVariant] Auto-initialization complete');
    }
  }

  /**
   * 自動初期化
   * DOM Ready時 + 遅延読み込み対応
   */
  $(document).ready(function() {
    // 初回初期化
    initializeHighVariantElements();

    // MutationObserverで動的に追加された要素も初期化
    const observer = new MutationObserver(function(mutations) {
      let shouldInit = false;
      mutations.forEach(function(mutation) {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1) { // ELEMENT_NODE
              if (node.querySelector && node.querySelector('[data-high-variant-mode="true"]')) {
                shouldInit = true;
              }
            }
          });
        }
      });
      if (shouldInit) {
        initializeHighVariantElements();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  });

  // グローバルに初期化関数を公開（手動呼び出し用）
  window.theme.initHighVariantElements = initializeHighVariantElements;

    })(jQuery);
  }

  // バージョン情報をグローバルに出力
  window.MISERU_HIGH_VARIANT_VERSION = '1.0.3-client-variant-calc';
  window.MISERU_HIGH_VARIANT_COMMIT = 'client-side-variant-calculation';
  console.log('[HighVariant] Version:', window.MISERU_HIGH_VARIANT_VERSION, 'Commit:', window.MISERU_HIGH_VARIANT_COMMIT);

  // 初期化開始
  initHighVariant();
})();
